import React from 'react';
import dynamic from 'next/dynamic';
import cx from 'classnames';

// Context

// Helpers
import { GetServerSideProps } from 'next';
import { ServerResponse } from 'http';
import type IncomingMessageWithCookies from '~/types/IncomingMessageWithCookies';
import { fetchNavHTML, fetchShopCategories } from '~/libs/server/nav';
import { fetchUser } from '~/libs/server/users';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { SecondaryNavType } from '~/components/Nav/types';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import { useIsPostAuthNav3MobileOrDesktop } from '~/hooks/useIsPostAuthNav3Active';

// Sections
import Header from '~/components/LP/CashGifts/components/Header';
import Nav from '~/components/Nav/Nav';
import Hero from '~/components/LP/CashGifts/components/Hero';
import CashFundCarousel from '~/components/LP/CashGifts/components/CashFundCarousel';
import GetMoreTextSection from '~/components/LP/CashGifts/components/GetMoreTextSection';
import TryHoneymoonImageSection from '~/components/LP/CashGifts/components/TryHoneymoonImageSection';
import AddPhotosImageSection from '~/components/LP/CashGifts/components/AddPhotosImageSection';
import SplitFundsImageSection from '~/components/LP/CashGifts/components/SplitFundsImageSection';
import FAQSection from '~/components/LP/CashGifts/components/FAQSection';
import ReadyWhenYouAre from '@zola/zola-ui/src/components/PreAuth/ReadyWhenYouAre';
import HowItWorks from '~/components/LP/CashGifts/components/HowItWorks';
import type { WStoreNavView } from '@zola/svc-web-api-ts-client';

import css from '~/pages/wedding-registry/how-cash-gifts-work/cash.module.less';

const Footer = dynamic(import('~/components/ui/Footer/Footer'), {
  ssr: false,
});

type CashFundsLPProps = {
  navHTML: string | null;
  shopCategories: WStoreNavView;
  userCtx: UserContext | null;
};

/** ******************************************************************************************
 *                                                                                           *
 *            This page should adhere to the web-preauth LP style guide:                     *
 *                                                                                           *
 * https://github.com/NewAmsterdamLabs/web-preauth/wiki/Pre-auth-Landing-Page-Style-Guide    *
 *                                                                                           *
 ****************************************************************************************** */

const CashFunds: React.FC<CashFundsLPProps> = ({ navHTML, shopCategories, userCtx }) => {
  const isNotLoggedIn = !userCtx || Boolean(userCtx.is_guest);

  const ctaLink = isNotLoggedIn ? '/onboard/new' : '/shop/honeymoon-cash-funds';
  const ctaText = isNotLoggedIn ? 'Build Your Registry' : 'Add Cash Funds';

  const isPostAuthNav3 = useIsPostAuthNav3MobileOrDesktop(userCtx);

  return (
    <>
      <Header />
      <Nav
        disablePrimaryNavCollapse
        showPromo
        activePrimaryLink={NAV_DATA.PRIMARY.REGISTRY}
        navHTML={navHTML}
        secondaryNavType={SecondaryNavType.REGISTRY}
        shopCategories={shopCategories}
        user={userCtx}
      />
      <div className={cx({ [css.cashContainer]: isPostAuthNav3 })}>
        <Hero ctaLink={ctaLink} ctaText={ctaText} />
        <CashFundCarousel ctaText={ctaText} ctaLink={ctaLink} />
        <HowItWorks isNotLoggedIn={isNotLoggedIn} />
        <GetMoreTextSection />
        <TryHoneymoonImageSection />
        <AddPhotosImageSection />
        <SplitFundsImageSection />
        <FAQSection />
        <ReadyWhenYouAre buttonText={ctaText} link={ctaLink} titleElement="h2" />
        <Footer />
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
  res: ServerResponse;
}) => {
  const [navHTML, userCtx, shopCategories] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchShopCategories(),
  ]);

  return {
    props: {
      navHTML,
      shopCategories,

      userCtx,
    },
  };
};

export default CashFunds;
